import type { IUserResponse } from '@escloud/types';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRegion } from '@/context';
import { useChangeEmail } from '@/hooks/use-current-user';
import { describeChangeEmailError, isExpectedChangeEmailRefusal } from '@/lib/change-email-errors';
import { errorIds, logError } from '@/lib/log-error';

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Correct a mistyped address while it is still unverified (ESCLOUD-333). Auth0's
 * signup form has a single email field, so this is the user's chance to fix a
 * typo without support. The server refuses once the address is verified; a
 * verified address changes through a later, more careful flow.
 *
 * `region` is optional: the banner and the clinical use gate omit it, so
 * `useChangeEmail(null)` falls back to the tenant context's region. The
 * payment page has no tenant yet, so it passes the intent's region explicitly
 * (ESCLOUD-334 folded the payment card's own dialog onto this one).
 */
export function ChangeEmailDialog({
    open,
    onOpenChange,
    currentEmail,
    region = null,
    onChanged,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentEmail: string;
    region?: string | null;
    onChanged?: (email: string) => void;
}) {
    const { refreshToken } = useRegion();
    const changeEmail = useChangeEmail(region);
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState<string | null>(null);

    const trimmed = email.trim().toLowerCase();
    const canSubmit =
        EMAIL_SHAPE.test(trimmed) && trimmed !== currentEmail.toLowerCase() && !changeEmail.isPending;

    const close = () => {
        setEmail('');
        setMessage(null);
        onOpenChange(false);
    };

    const submit = async () => {
        if (!canSubmit) return;
        setMessage(null);
        let user: IUserResponse;
        try {
            user = await changeEmail.mutateAsync({ email: trimmed });
        } catch (caught) {
            const described = describeChangeEmailError(caught);
            if (described.kind === 'info') {
                await refreshToken().catch(() => undefined);
                toast.info(described.message);
                close();
                return;
            }
            setMessage(described.message);
            if (!isExpectedChangeEmailRefusal(caught)) {
                logError(errorIds.EMAIL_CHANGE_FAILED, 'Email change failed', { error: caught });
            }
            return;
        }

        // The change is done server-side from here on: nothing below may turn
        // it into a "could not be changed" message (ESCLOUD-334).
        // The email claim on the token is stale until re-minted.
        await refreshToken().catch(() => undefined);
        toast.success('Address changed', {
            description: `A verification email was sent to ${trimmed}.`,
        });
        close();
        try {
            onChanged?.(user.email ?? trimmed);
        } catch (error) {
            logError(errorIds.EMAIL_CHANGE_FAILED, 'Email change follow-up failed', { error });
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Change your email address</DialogTitle>
                    <DialogDescription>
                        Receipts and the verification email go to this address. Currently {currentEmail}.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-1.5">
                    <Label htmlFor="change-email-input">New email address</Label>
                    <Input
                        id="change-email-input"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        disabled={changeEmail.isPending}
                    />
                    {message && (
                        <p role="alert" className="m-0 text-sm text-destructive">
                            {message}
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={close} disabled={changeEmail.isPending}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={() => void submit()} disabled={!canSubmit}>
                        {changeEmail.isPending ? 'Changing…' : 'Change address'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
